import React, { useEffect, useMemo, useState } from 'react';
import { QuestionRendererProps } from './types';
import { motion } from 'framer-motion';
import { useEdgeTriggeredSubmit } from './useEdgeTriggeredSubmit';
import { hapticManager } from '../../../utils/hapticManager';
import { FALLBACK_COLOR } from './rendererUtils';
export function FractionStripBuilderRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate,
  moduleColor
}: QuestionRendererProps) {
  const color = moduleColor || FALLBACK_COLOR;
  const config = question.data?.fractionStripBuilder || question.data || {};
  const targetDenominator = (config as any).denominator || 1;
  const targetNumerator = (config as any).targetNumerator || 0;
  const mode = (config as any).mode || 'both'; // 'divide' | 'shade' | 'both'
  // State
  // divisions: number of segments the strip is currently divided into
  const [divisions, setDivisions] = useState<number>(mode === 'shade' ? targetDenominator : 1);
  // shaded: array of indices of shaded segments
  const [shaded, setShaded] = useState<number[]>([]);
  useEffect(() => {
    // Ready if we have divisions and (if shading is required) some shaded segments
    const isReady = divisions > 1 && (mode === 'divide' || shaded.length > 0);
    onReadyChange?.(isReady);
  }, [divisions, shaded.length, mode, onReadyChange]);
  const answerPayload = useMemo(() => JSON.stringify({
    divisions,
    shaded
  }), [divisions, shaded]);
  useEdgeTriggeredSubmit(answerPayload, shouldValidate, onAnswer, question.id);
  const handleDivide = (newDivisions: number) => {
    if (shouldValidate || mode === 'shade') return;
    if (divisions !== newDivisions) {
      hapticManager.hapticMedium();
    }
    setDivisions(newDivisions);
    // Reset shading when divisions change
    setShaded([]);
  };
  const toggleShade = (index: number) => {
    if (shouldValidate || mode === 'divide') return;
    hapticManager.hapticLight();
    setShaded(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };
  const isCorrect = divisions === targetDenominator && (mode === 'divide' || shaded.length === targetNumerator);
  return <div className="w-full max-w-2xl mx-auto flex flex-col items-center gap-8" data-id="element-11524">
      {/* Controls for dividing */}
      {(mode === 'divide' || mode === 'both') && <div className="flex flex-col items-center gap-4 w-full" data-id="element-11525">
          <p className="text-sm font-semibold text-slate-500 text-center px-4" data-id="element-11526">
            Verdeel de strook in gelijke stukken:
          </p>
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3 px-2" data-id="element-11527">
            {[2, 3, 4, 5, 6, 8, 10, 12].map(num => <button key={num} onClick={() => handleDivide(num)} disabled={shouldValidate} className={`w-11 h-11 sm:w-12 sm:h-12 rounded-full font-bold text-base sm:text-lg transition-colors ${divisions === num ? 'text-white shadow-md' : 'bg-slate-100 text-slate-600 hover:bg-slate-200 active:bg-slate-300'} ${shouldValidate ? 'opacity-50 cursor-not-allowed' : ''}`} style={divisions === num ? {
          backgroundColor: color
        } : undefined} data-id="element-11528">
                {num}
              </button>)}
          </div>
        </div>}

      {/* The Strip */}
      <div className="w-full flex flex-col gap-3 px-2 sm:px-0" data-id="element-11529">
        {(mode === 'shade' || mode === 'both') && <p className="text-sm font-semibold text-slate-500 text-center" data-id="element-11530">
            Kleur de juiste stukken in door erop te klikken:
          </p>}

        <div className="relative w-full h-20 sm:h-24 bg-slate-100 rounded-xl border-2 border-slate-300 overflow-hidden flex shadow-inner" data-id="element-11531">
          {Array.from({
          length: divisions
        }).map((_, i) => {
          const isShaded = shaded.includes(i);
          return <motion.button key={i} onClick={() => toggleShade(i)} disabled={shouldValidate || mode === 'divide'} className={`flex-1 h-full border-r-2 border-slate-300 last:border-r-0 transition-colors relative group ${mode === 'divide' ? 'cursor-default' : 'cursor-pointer'}`} style={{
            backgroundColor: isShaded ? shouldValidate ? isCorrect ? '#10b981' : '#ef4444' : color : 'transparent'
          }} whileTap={shouldValidate || mode === 'divide' ? {} : {
            scale: 0.95
          }} data-id="element-11532">
                {!shouldValidate && mode !== 'divide' && !isShaded && <div className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity" style={{
              backgroundColor: color
            }} data-id="element-11533" />}
              </motion.button>;
        })}
        </div>

        {/* Fraction Labels — hidden when divisions get too dense to be legible.
            At divisions > 8 the per-cell width drops below ~40px on mobile and
            "1/12" labels overlap and become visual noise. We still show the
            denominator-context via the stip selector above and the shaded count. */}
        {divisions <= 8 && <div className="w-full flex justify-between px-1" data-id="element-11534">
            {Array.from({
          length: divisions
        }).map((_, i) => <div key={i} className="flex-1 flex justify-center min-w-0" data-id="element-11535">
                <span className={`font-medium text-slate-400 tabular-nums ${divisions >= 6 ? 'text-[10px] sm:text-xs' : 'text-xs sm:text-sm'}`} data-id="element-11536">
                  1/{divisions}
                </span>
              </div>)}
          </div>}
      </div>
    </div>;
}